import type { StartupState } from '@/domain/Models';
import type { ArticleRepositoryImpl } from './ArticleRepositoryImpl';
import type { CorpusRepositoryImpl } from './CorpusRepositoryImpl';
import {
  catalogPackages,
  loadCatalog,
  requestPersistentStorage,
  type CatalogPackage,
} from './BundledLoader';
import { sha256Hex } from './Crypto';
import { getMeta, setMeta } from './Database';
import { isStandalone } from '@/pwa/install';

export function activatedShaKey(sha256: string): string {
  return `activated_sha:${sha256.toLowerCase()}`;
}

/**
 * First-launch corpus download state machine.
 *
 * Catalog v2 lists every required pack. Each pack is downloaded, SHA-checked
 * and activated independently. A failed pack does not roll back packs that
 * already succeeded. Dev/preview fall back to bundled filenames.
 */
export class CorpusDownloader {
  constructor(
    private readonly corpus: CorpusRepositoryImpl,
    private readonly article: ArticleRepositoryImpl,
  ) {}

  async run(onUpdate: (s: StartupState) => void): Promise<void> {
    let catalogPackagesList: CatalogPackage[] | null = null;
    try {
      const catalog = await loadCatalog();
      catalogPackagesList = catalogPackages(catalog);
    } catch {
      catalogPackagesList = null;
    }

    if (catalogPackagesList && catalogPackagesList.length > 0) {
      const pending: CatalogPackage[] = [];
      for (const pkg of catalogPackagesList) {
        if ((await getMeta(activatedShaKey(pkg.sha256))) === null) pending.push(pkg);
      }
      if (pending.length === 0) return;
      if (!isStandalone()) {
        onUpdate({
          phase: 'needs-install',
          progress: 0,
          message: '请先将应用添加到主屏幕',
          error: null,
        });
        return;
      }
      onUpdate({ phase: 'checking-storage', progress: 6, message: '检查可用空间…', error: null });
      await requestPersistentStorage();
      const total = pending.length;
      for (let i = 0; i < pending.length; i += 1) {
        const pkg = pending[i]!;
        const base = Math.floor((i / total) * 90);
        const span = Math.floor(90 / total);
        await this.activateCatalogPackage(pkg, base, span, onUpdate);
      }
      return;
    }

    const corpusActive = await getMeta('active_corpus_content_version');
    const articlesActive = await getMeta('articles_bundled_version');
    if (corpusActive !== null && articlesActive !== null) return;

    if (!isStandalone()) {
      onUpdate({
        phase: 'needs-install',
        progress: 0,
        message: '请先将应用添加到主屏幕',
        error: null,
      });
      return;
    }

    onUpdate({ phase: 'checking-storage', progress: 6, message: '检查可用空间…', error: null });
    await requestPersistentStorage();
    await this.installBundled(corpusActive === null, articlesActive === null, onUpdate);
  }

  private async activateCatalogPackage(
    pkg: CatalogPackage,
    base: number,
    span: number,
    onUpdate: (s: StartupState) => void,
  ): Promise<void> {
    const label = pkg.kind === 'corpus' ? '语料' : '文章';
    onUpdate({
      phase: 'downloading-corpus',
      progress: base + 2,
      message: `准备下载${label}…`,
      error: null,
    });
    const bytes = await this.download(pkg, label, (p, msg) =>
      onUpdate({
        phase: 'downloading-corpus',
        progress: base + Math.floor((p / 100) * span * 0.7),
        message: msg,
        error: null,
      }),
    );
    const sha = await sha256Hex(bytes);
    if (sha.toLowerCase() !== pkg.sha256.toLowerCase()) {
      throw new Error(
        pkg.kind === 'corpus' ? 'CORPUS_DOWNLOAD_CHECKSUM_MISMATCH' : 'ARTICLE_DOWNLOAD_CHECKSUM_MISMATCH',
      );
    }
    onUpdate({
      phase: pkg.kind === 'corpus' ? 'loading-corpus' : 'loading-articles',
      progress: base + Math.floor(span * 0.72),
      message: `写入${label}…`,
      error: null,
    });
    const result =
      pkg.kind === 'corpus'
        ? await this.corpus.activateDownloaded(bytes, pkg.sha256, (p, msg) =>
            onUpdate({
              phase: 'loading-corpus',
              progress: base + Math.floor(span * 0.72 + (p / 100) * span * 0.25),
              message: msg,
              error: null,
            }),
          )
        : await this.article.activateDownloaded(bytes, pkg.sha256, (p, msg) =>
            onUpdate({
              phase: 'loading-articles',
              progress: base + Math.floor(span * 0.72 + (p / 100) * span * 0.25),
              message: msg,
              error: null,
            }),
          );
    if (!result.ok) {
      throw new Error(
        result.errorCode ??
          (pkg.kind === 'corpus' ? 'CORPUS_DOWNLOAD_INSTALL_FAILED' : 'ARTICLE_DOWNLOAD_INSTALL_FAILED'),
      );
    }
    await setMeta(activatedShaKey(pkg.sha256), '1');
  }

  private async installBundled(
    needCorpus: boolean,
    needArticles: boolean,
    onUpdate: (s: StartupState) => void,
  ): Promise<void> {
    if (needCorpus) {
      onUpdate({ phase: 'loading-corpus', progress: 15, message: '激活内置语料（首次较慢）…', error: null });
      await this.corpus.ensureBundledActivated((p, msg) =>
        onUpdate({
          phase: 'loading-corpus',
          progress: 15 + Math.floor(p * 0.4),
          message: msg,
          error: null,
        }),
      );
    }
    if (needArticles) {
      onUpdate({ phase: 'loading-articles', progress: 60, message: '激活内置文章…', error: null });
      await this.article.ensureBundledActivated((p, msg) =>
        onUpdate({
          phase: 'loading-articles',
          progress: 60 + Math.floor(p * 0.35),
          message: msg,
          error: null,
        }),
      );
    }
  }

  private async download(
    entry: CatalogPackage,
    label: string,
    onProgress: (p: number, msg: string) => void,
  ): Promise<Uint8Array> {
    const totalMb = Math.ceil(entry.sizeBytes / (1024 * 1024));
    onProgress(10, `下载${label}（约 ${totalMb}MB）…`);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5 * 60 * 1000);
    const maxBytes = (Math.max(entry.sizeBytes, 1) * 3) / 2;
    let response: Response;
    try {
      response = await fetch(`/content/${entry.fileName}`, { signal: controller.signal });
    } finally {
      clearTimeout(timeout);
    }
    if (!response.ok) throw new Error(`DOWNLOAD_HTTP_${response.status}`);

    if (!response.body) {
      const buffer = await response.arrayBuffer();
      const out = new Uint8Array(buffer);
      if (out.byteLength > maxBytes) throw new Error('DOWNLOAD_SIZE_EXCEEDS_EXPECTED');
      return out;
    }

    const reader = response.body.getReader();
    const total = entry.sizeBytes;
    const chunks: Uint8Array[] = [];
    let received = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        received += value.byteLength;
        if (received > maxBytes) {
          controller.abort();
          throw new Error('DOWNLOAD_SIZE_EXCEEDS_EXPECTED');
        }
      }
      const pct = Math.min(96, Math.floor((received / Math.max(1, total)) * 100));
      const mb = (received / (1024 * 1024)).toFixed(0);
      onProgress(10 + Math.floor(pct * 0.62), `下载${label}… ${mb}/${totalMb} MB`);
    }
    const out = new Uint8Array(received);
    let offset = 0;
    for (const chunk of chunks) {
      out.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return out;
  }
}
